// src/components/resumo/ReviewerPanel.tsx
import React, { useState, useEffect, useRef } from 'react';
import { X, CheckCircle, AlertTriangle, Circle, MessageSquare } from 'lucide-react';
import './ReviewerPanel.css';

export type PageStatus = 'revised' | 'attention' | 'untouched';

export interface PageNote {
    pageId: string;
    note: string;
    status: PageStatus;
}

interface Props {
    deckId: string;
    subdeckId: string;
    darkMode: boolean;
    currentPageId: string;
    pageNotes: PageNote[];
    reviewNote: string;
    onReviewNoteChange: (v: string) => void;
    onPageNotesChange: (notes: PageNote[]) => void;
    onClose: () => void;
}

const STATUS_OPTIONS: { value: PageStatus; label: string; Icon: React.ElementType }[] = [
    { value: 'revised',   label: 'Revisada',  Icon: CheckCircle },
    { value: 'attention', label: 'Atenção',    Icon: AlertTriangle },
    { value: 'untouched', label: 'Intocada',   Icon: Circle },
];

const ReviewerPanel: React.FC<Props> = ({
    deckId,
    subdeckId,
    darkMode,
    currentPageId,
    pageNotes,
    reviewNote,
    onReviewNoteChange,
    onPageNotesChange,
    onClose,
}) => {
    const token = localStorage.getItem('accessToken');
    const panelRef = useRef<HTMLDivElement>(null);

    const currentNote = pageNotes.find(n => n.pageId === currentPageId);
    const [localNote, setLocalNote] = useState(currentNote?.note ?? '');
    const [localStatus, setLocalStatus] = useState<PageStatus>(currentNote?.status ?? 'untouched');
    const [saving, setSaving] = useState(false);
    const [savedFeedback, setSavedFeedback] = useState(false);

    // Sincroniza campos locais quando muda de página
    useEffect(() => {
        const n = pageNotes.find(p => p.pageId === currentPageId);
        setLocalNote(n?.note ?? '');
        setLocalStatus(n?.status ?? 'untouched');
    }, [currentPageId, pageNotes]);

    // Fecha ao clicar fora
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [onClose]);

    const savePageNote = async () => {
        setSaving(true);
        try {
            await fetch(`/api/apostila-draft/${deckId}/${subdeckId}/page-note`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ pageId: currentPageId, note: localNote, status: localStatus })
            });

            // Atualiza estado local do pai
            const updated = pageNotes.filter(n => n.pageId !== currentPageId);
            updated.push({ pageId: currentPageId, note: localNote, status: localStatus });
            onPageNotesChange(updated);

            setSavedFeedback(true);
            setTimeout(() => setSavedFeedback(false), 2000);
        } catch {
            // silencioso — dados já foram atualizados localmente
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={`rvp-panel ${darkMode ? 'dark' : ''}`} ref={panelRef}>
            <div className="rvp-header">
                <span className="rvp-title">
                    <MessageSquare size={14} />
                    Painel de Revisão
                </span>
                <button className="rvp-close" onClick={onClose}><X size={15} /></button>
            </div>

            {/* ── Seção: Esta Página ── */}
            <div className="rvp-section">
                <div className="rvp-section-label">Esta Página</div>

                <div className="rvp-status-row">
                    {STATUS_OPTIONS.map(({ value, label, Icon }) => (
                        <button
                            key={value}
                            className={`rvp-status-btn rvp-status-${value} ${localStatus === value ? 'active' : ''}`}
                            onClick={() => setLocalStatus(value)}
                        >
                            <Icon size={13} />
                            {label}
                        </button>
                    ))}
                </div>

                <textarea
                    className="rvp-textarea"
                    rows={4}
                    placeholder="Deixe uma nota sobre esta página (opcional)..."
                    value={localNote}
                    onChange={e => setLocalNote(e.target.value)}
                />

                <button
                    className={`rvp-save-btn ${savedFeedback ? 'saved' : ''}`}
                    onClick={savePageNote}
                    disabled={saving}
                >
                    {savedFeedback ? '✓ Nota salva' : saving ? 'Salvando...' : 'Salvar nota da página'}
                </button>
            </div>

            <div className="rvp-divider" />

            {/* ── Seção: Revisão Geral ── */}
            <div className="rvp-section">
                <div className="rvp-section-label">Revisão Geral</div>
                <textarea
                    className="rvp-textarea rvp-textarea-large"
                    rows={6}
                    placeholder="Escreva um resumo geral da sua revisão para o administrador. O que você mudou, o que ficou com dúvida, o que precisa de atenção especial..."
                    value={reviewNote}
                    onChange={e => onReviewNoteChange(e.target.value)}
                />
                <p className="rvp-hint">Esta nota é salva junto com o rascunho ao clicar em Salvar.</p>
            </div>
        </div>
    );
};

export default ReviewerPanel;
